<template>
  <div>
    <div style="background-color: #212121" class="white--text">
      <v-data-table
        dark
        :search="search"
        :loading="!finishedLoading"
        :headers="groupfitnessCoursesTable"
        no-data-text="Keine Trainer gefunden..."
        loading-text="Trainerstatistiken werden geladen..."
        :items="filteredDashboardTrainerInfos"
        :items-per-page="8"
        class="elevation-5 trainer-statistics__table">
        <template v-slot:top>
          <v-row>
            <v-col style="flex-grow: 2">
              <v-text-field v-model="search" class="mx-4" label="Suche"></v-text-field>
            </v-col>
            <v-col style="align-self: center">
              <v-tooltip bottom>
                <div class="d-flex flex-column">
                  <span>Ausgeblendete Mitarbeiter (kein Vertrag, TP, und Anamnese):</span>
                  <p v-for="trainer of notShownTrainers" :key="'notshown' + trainer._id">
                    {{ trainer.fullName }}
                  </p>
                  <span v-if="!notShownTrainers.length">Es wurde kein Trainer ausgeblendet</span>
                </div>
                <template v-slot:activator="{ props, on }">
                  <v-btn v-bind="props" v-on="on" icon color="white">
                    <v-icon color="white">mdi-information</v-icon>
                  </v-btn>
                </template>
              </v-tooltip>
            </v-col>
          </v-row>
        </template>

        <!-- Stellenprozent (unverändert) -->
        <template v-slot:item.percentage="{ item }">
          <v-tooltip bottom>
            <div class="d-flex flex-column">
              <span>Stellenprozent Berechnung:</span>
              <span v-for="(pItem, idx) in item.allPercentages" :key="pItem._id + item.fullName + idx">
                {{ (storeStudios.find((s) => s._id === pItem._id)?.name || "zus. Team") + " " + pItem.percentage + "%" }}
              </span>
            </div>
            <template v-slot:activator="{ props, on }">
              <v-chip color="grey" v-bind="props" v-on="on" style="min-width: 50%" class="justify-content-center font-weight-bold" dark>
                {{ item.percentage + "%" }}
              </v-chip>
            </template>
          </v-tooltip>
        </template>

        <!-- Verträge mit Ziel -->
        <template v-slot:item.contracts="{ item }">
          <v-tooltip bottom>
            <div>
              <p v-for="(count, name) in item.studios" :key="name">{{ name }}: {{ count }}</p>
            </div>
            <template v-slot:activator="{ props, on }">
              <v-chip
                v-bind="props"
                v-on="on"
                :color="getTargetColor(item.contracts, item.trainerGoal?.goals.contracts)"
                style="min-width: 50%"
                class="justify-content-center font-weight-bold"
                dark>
                {{ item.contracts }} / {{ item.trainerGoal?.goals.contracts ?? "-" }}
              </v-chip>
            </template>
          </v-tooltip>
        </template>

        <!-- Ø Pakete (unverändert) -->
        <template v-slot:item.avgPackages="{ item }">
          <v-tooltip bottom>
            <div>
              <p>Gesamt verkauft: {{ Math.round(item.avgPackages * item.contracts) }}</p>
            </div>
            <template v-slot:activator="{ props, on }">
              <v-chip
                v-bind="props"
                v-on="on"
                :color="getPackageColor(item.avgPackages)"
                style="min-width: 50%"
                class="justify-content-center font-weight-bold"
                dark>
                {{ item.avgPackages }}
              </v-chip>
            </template>
          </v-tooltip>
        </template>

        <!-- Pakete (Anzahl) mit Ziel -->
        <template v-slot:item.packageCount="{ item }">
          <v-chip
            v-bind="props"
            v-on="on"
            :color="getTargetColor(item.packageCount, item.trainerGoal?.goals.packages)"
            style="min-width: 50%"
            class="justify-content-center font-weight-bold"
            dark>
            {{ item.packageCount }} / {{ item.trainerGoal?.goals.packages ?? "-" }}
          </v-chip>
        </template>

        <!-- Anamnesebögen mit Ziel -->
        <template v-slot:item.medicalHistory="{ item }">
          <v-tooltip bottom>
            <div></div>
            <template v-slot:activator="{ props, on }">
              <v-chip
                v-bind="props"
                v-on="on"
                :color="getTargetColor(item.medicalHistory, item.trainerGoal?.goals.anamneses)"
                style="min-width: 50%"
                class="justify-content-center font-weight-bold"
                dark>
                {{ item.medicalHistory }} / {{ item.trainerGoal?.goals.anamneses ?? "-" }}
              </v-chip>
            </template>
          </v-tooltip>
        </template>

        <!-- Trainingspläne mit Ziel -->
        <template v-slot:item.workouts="{ item }">
          <v-tooltip bottom>
            <span>Trainingsplan-Historie öffnen</span>
            <template v-slot:activator="{ props, on }">
              <v-chip
                v-bind="props"
                v-on="on"
                :color="getTargetColor(item.workouts, item.trainerGoal?.goals.workouts)"
                @click="openWorkoutHistory(item)"
                style="min-width: 50%"
                class="justify-content-center font-weight-bold"
                dark>
                {{ item.workouts }} / {{ item.trainerGoal?.goals.workouts ?? "-" }}
                <v-icon v-if="canViewWorkoutHistory(item)" right small>mdi-history</v-icon>
              </v-chip>
            </template>
          </v-tooltip>
        </template>

        <!-- Versch. Kunden TP mit Ziel -->
        <template v-slot:item.workoutsPerCustomer="{ item }">
          <v-tooltip bottom>
            <div></div>
            <template v-slot:activator="{ props, on }">
              <v-chip
                v-bind="props"
                v-on="on"
                :color="getTargetColor(item.workoutsPerCustomer, item.trainerGoal?.goals.workoutsPerCustomer)"
                style="min-width: 50%"
                class="justify-content-center font-weight-bold"
                dark>
                {{ item.workoutsPerCustomer }} / {{ item.trainerGoal?.goals.workoutsPerCustomer ?? "-" }}
              </v-chip>
            </template>
          </v-tooltip>
        </template>

        <!-- Kundeninteraktionen mit Ziel -->
        <template v-slot:item.interactions.totalInteractions="{ item }">
          <v-tooltip bottom>
            <div></div>
            <template v-slot:activator="{ props, on }">
              <v-chip
                v-bind="props"
                v-on="on"
                :color="getTargetColor(item.interactions.totalInteractions, item.trainerGoal?.goals.interactions)"
                style="min-width: 50%"
                class="justify-content-center font-weight-bold"
                dark>
                {{ item.interactions.totalInteractions }} /
                {{ item.trainerGoal?.goals.interactions ?? "-" }}
              </v-chip>
            </template>
          </v-tooltip>
        </template>

        <!-- Termine mit Ziel -->
        <template v-slot:item.appointments="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ props, on }">
              <v-chip
                v-bind="props"
                v-on="on"
                :color="getTargetColor(item.trainerGoal?.goals.appointmentsActual, item.trainerGoal?.goals.appointmentsTarget)"
                style="min-width: 50%"
                class="justify-content-center font-weight-bold"
                dark>
                {{ item.trainerGoal?.goals.appointmentsActual ?? 0 }} / {{ item.trainerGoal?.goals.appointmentsTarget ?? "-" }}
              </v-chip>
            </template>
          </v-tooltip>
        </template>

        <!-- Upgrades mit Ziel -->
        <template v-slot:item.upgrades="{ item }">
          <v-chip
            v-bind="props"
            v-on="on"
            :color="getTargetColor(item.upgrades, item.trainerGoal?.goals.upgrades)"
            style="min-width: 50%"
            class="justify-content-center font-weight-bold"
            dark>
            {{ item.upgrades }} / {{ item.trainerGoal?.goals.upgrades ?? "-" }}
          </v-chip>
        </template>
      </v-data-table>

      <!-- Trainer Cards (unverändert, mit Upgrades ergänzt) -->
      <v-row>
        <v-col
          style="margin-top: 100px"
          :cols="$vuetify.breakpoint.mobile ? 6 : 3"
          :key="'trainer' + index"
          v-for="(trainer, index) in filteredDashboardTrainerInfos">
          <v-card
            dark
            hover
            style="height: 100%"
            class="d-flex flex-column justify-content-center text-center rounded-xl"
            @click="openTargetDialog(trainer)">
            <v-avatar style="position: absolute; top: -62px; left: 0; right: 0" class="employee-manager__card-avatar ma-auto" color="grey" size="125">
              <img :src="trainer.picture ? '/img/profilbilder' + trainer.picture : '/img/einmalig/imageUploadPlaceholderPFP.jpg'" />
            </v-avatar>
            <v-card-title class="justify-content-center text-break" style="margin-top: 55px">
              {{ trainer.fullName }}
            </v-card-title>
            <v-card-subtitle>
              <v-chip
                class="mx-1"
                :class="{ primary: storeStudios.find((s) => s._id === std)?._id === trainer.mainStudio }"
                v-for="std of trainer.studio.split(',')"
                :key="trainer._id + std">
                {{ std }}
              </v-chip>
            </v-card-subtitle>

            <!-- Anzeige der hinterlegten Ziele -->
            <v-card-text>
              <div v-if="trainer.trainerGoal?.goals">
                <strong>Ziele:</strong>
                <v-list dense>
                  <v-list-item>Verträge: {{ trainer.trainerGoal.goals.contracts }}</v-list-item>
                  <v-list-item>Anamnesebögen: {{ trainer.trainerGoal.goals.anamneses }}</v-list-item>
                  <v-list-item>Anzahl Pakete: {{ trainer.trainerGoal.goals.packages }}</v-list-item>
                  <v-list-item>Trainingspläne: {{ trainer.trainerGoal.goals.workouts }}</v-list-item>
                  <v-list-item>Versch. Kunden TP: {{ trainer.trainerGoal.goals.workoutsPerCustomer }}</v-list-item>
                  <v-list-item>Kundeninteraktionen: {{ trainer.trainerGoal.goals.interactions }}</v-list-item>
                  <v-list-item>Upgrades: {{ trainer.trainerGoal.goals.upgrades }}</v-list-item>
                  <v-list-item
                    >Termine (Ziel/Ist): {{ trainer.trainerGoal.goals.appointmentsTarget }} /
                    {{ trainer.trainerGoal.goals.appointmentsActual }}</v-list-item
                  >
                </v-list>
              </div>
              <div v-else>
                <em>Keine Ziele hinterlegt</em>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Dialog zur Zielsetzung (unverändert, mit Upgrades ergänzt) -->
      <trainer-workout-history-dialog
        v-if="workoutHistoryTrainer"
        :trainer="workoutHistoryTrainer"
        :studios="studios"
        :range="datePickerRange"
        @close="workoutHistoryTrainer = null" />
      <v-dialog v-model="showTargetDialog" max-width="500px">
        <v-card dark v-if="selectedTrainer">
          <v-card-title>Ziele für {{ selectedTrainer.fullName }} im {{ $moment(datePickerRange[0]).format("MMM") }}</v-card-title>
          <v-row no-gutters>
            <v-col class="text-center" cols="6"
              ><span class="font-weight-bold">Sollstunden</span>
              <p>
                {{ workHours }}
              </p>
            </v-col>
            <v-col class="text-center" cols="6"
              ><span class="font-weight-bold">Aktuelle Überstunden</span>
              <p>{{ selectedTrainer.overTime }} Stunden</p>
            </v-col>
            <v-col v-if="false" class="text-center" cols="4">
              <span class="font-weight-bold">Ferientage</span>
              <p>{{ vacationDays }} Tage</p>
            </v-col>
          </v-row>
          <v-card-text>
            <v-form>
              <v-text-field v-model.number="targets[selectedTrainer._id].contracts" label="Verträge" type="number" />
              <v-text-field v-model.number="targets[selectedTrainer._id].anamneses" label="Anamnesebögen" type="number" />
              <v-text-field v-model.number="targets[selectedTrainer._id].packages" label="Anzahl Pakete" type="number" />
              <v-text-field v-model.number="targets[selectedTrainer._id].workouts" label="Trainingspläne" type="number" />
              <v-text-field v-model.number="targets[selectedTrainer._id].workoutsPerCustomer" label="Versch. Kunden TP" type="number" />
              <v-text-field v-model.number="targets[selectedTrainer._id].interactions" label="Kundeninteraktionen" type="number" />
              <v-text-field v-model.number="targets[selectedTrainer._id].upgrades" label="Upgrades" type="number" />
              <v-text-field v-model.number="targets[selectedTrainer._id].appointmentsTarget" label="Termine (Ziel)" type="number" />
              <v-text-field v-model.number="targets[selectedTrainer._id].appointmentsActual" label="Termine (Ist)" type="number" />
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="closeDialog">Abbrechen</v-btn>
            <v-btn color="primary" text @click="saveTargets">Speichern</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script>
import api from "../../../api"
import { mapState } from "vuex"
import userService from "@/services/userService.js"
import TrainerWorkoutHistoryDialog from "./TrainerWorkoutHistoryDialog.vue"

export default {
  name: "dashboard",
  components: { TrainerWorkoutHistoryDialog },
  props: {
    studios: { type: Array },
    datePickerRange: { type: Array },
  },
  data() {
    return {
      userService,
      tab: 0,
      notShownTrainers: [],
      activeSorting: "",
      search: "",
      vacationDays: [],

      percentagesTo100: false,
      dashboardTrainerInfos: [],
      finishedLoading: false,
      showTargetDialog: false,
      selectedTrainer: null,
      workoutHistoryTrainer: null,
      targets: {},
      workHours: "",
      sortItems: [
        { text: "Anz. Anamnese", value: "medicalHistory" },
        { text: "Interaktionen", value: "interactions" },
        { text: "Anz. Trainingspläne", value: "workouts" },
        { text: "Anz. Verträge", value: "contracts" },
        { text: "Ø Pakete", value: "avgPackages" },
        { text: "Pakete", value: "packageCount" },
      ],
      groupfitnessCoursesTable: [
        { text: "Name", value: "fullName" },
        { text: "Stellenprozent", value: "percentage" },
        { text: "Verträge", value: "contracts" },
        { text: "Pakete", value: "packageCount" },
        { text: "Anamnesebögen", value: "medicalHistory" },
        { text: "Trainingspläne", value: "workouts" },
        { text: "Versch. Kunden TP", value: "workoutsPerCustomer" },
        { text: "Kundeninteraktionen", value: "interactions.totalInteractions" },
        { text: "Termine", value: "appointments" },
        { text: "Upgrades", value: "upgrades" },
      ],
      trainerVacations: [],
    }
  },
  async created() {
    this.dashboardTrainerInfos = await api.fetchTrainerDashboardInfos({
      studios: this.studios.map((s) => s._id),
      range: this.datePickerRange,
    })
    // this.trainerVacations = await api.fetchHolidayEntryEmployeeStudioManagement(this.selectedStudioSingle, "accepted")

    this.finishedLoading = true
  },
  methods: {
    canViewWorkoutHistory(trainer) {
      return String(trainer._id) === String(userService.getUserId()) || userService.hasRight("stat_admin") || userService.hasRight("stat_viewstudio")
    },
    openWorkoutHistory(trainer) {
      if (this.canViewWorkoutHistory(trainer)) this.workoutHistoryTrainer = trainer
    },
    getVacationDaysForTrainer(trainer) {
      const month = this.$moment(this.datePickerRange[0]).format("YYYY-MM")
      // alle Urlaubs-Entries für diesen Trainer
      const entries = this.trainerVacations.filter((v) => v.employee.fullName === trainer.fullName)
      // Summe der duration-Werte für alle dates im richtigen Monat
      return entries.reduce((total, entry) => {
        return (
          total +
          entry.dates.reduce((sum, d) => {
            return this.$moment(d.date).format("YYYY-MM") === month && d.duration > 0 ? sum + d.duration : sum
          }, 0)
        )
      }, 0)
    },
    vacationDaysForMonth(studio) {
      if (studio._id) studio = studio._id
      let fullStudio = this.storeStudios.find((std) => std._id === studio)
      if (!fullStudio || !fullStudio.vacationDays) return []
      return fullStudio.vacationDays.filter((day) => this.$moment(this.month).format("YYYY-MM") === this.$moment(day.date).format("YYYY-MM"))
    },
    openTargetDialog(trainer) {
      if (userService.hasRole("616616c4c659c875cad63525") || userService.hasRole("61661791c659c875cad63527")) return
      this.selectedTrainer = trainer
      this.vacationDays = this.getVacationDaysForTrainer(trainer)

      this.workHours =
        Math.round(
          (this.calcBusinessDays - this.vacationDaysForMonth(trainer?.mainStudio?._id || trainer.studio[0]).length) *
            ((8.4 * trainer.percentage) / 100) *
            1000
        ) /
          1000 +
        " Stunden (" +
        trainer.percentage +
        "%)"
      const id = trainer._id
      if (trainer.trainerGoal?.goals) {
        this.$set(this.targets, id, { ...trainer.trainerGoal.goals })
      } else if (!this.targets[id]) {
        this.$set(this.targets, id, {
          contracts: null,
          anamneses: null,
          packages: null,
          workouts: null,
          workoutsPerCustomer: null,
          interactions: null,
          upgrades: null,
          appointmentsTarget: null,
          appointmentsActual: null,
        })
      }
      this.showTargetDialog = true
    },
    closeDialog() {
      this.showTargetDialog = false
      this.selectedTrainer = null
    },
    async saveTargets() {
      const t = this.targets[this.selectedTrainer._id]

      // TODO: API-Call zum Speichern der Ziele:
      await api.saveTrainerGoalsForMonth({
        goals: t,
        trainer: this.selectedTrainer._id,
        month: this.$moment(this.datePickerRange[0]).format("YYYY-MM"),
      })

      const trainer = this.dashboardTrainerInfos.find((t) => t._id === this.selectedTrainer._id)
      this.$set(trainer, "trainerGoal", {
        month: this.$moment(this.datePickerRange[0]).format("YYYY-MM"),
        goals: t,
      })
      this.closeDialog()
    },
    getTargetColor(actual, target) {
      if (target == null || target === 0) return "grey"
      const ratio = actual / target
      if (ratio <= 0.5) return "red"
      if (ratio < 1) return "orange"
      return "green"
    },
    // Farb-Helper für Ø Pakete, Quote bleiben unverändert
    getPackageColor(val) {
      if (val > this.packagesOnAverage + 0.5) return "green"
      if (val < this.packagesOnAverage - 0.5) return "red"
      return "orange"
    },
  },
  computed: {
    ...mapState("studioStore", ["storeStudios"]),
    calcBusinessDays() {
      var day = this.$moment(this.datePickerRange[0]).startOf("month")
      var businessDays = 0

      while (day.isSameOrBefore(this.$moment(this.datePickerRange[0]).endOf("month"), "day")) {
        if (day.day() != 0 && day.day() != 6) businessDays++
        day.add(1, "d")
      }
      return businessDays
    },
    contractsOnAverage() {
      return this.dashboardTrainerInfos.reduce((a, c) => a + c.contracts, 0) / this.dashboardTrainerInfos.length
    },
    packagesOnAverage() {
      return this.dashboardTrainerInfos.reduce((a, c) => a + c.avgPackages, 0) / this.dashboardTrainerInfos.length
    },
    medicalHistoriesOnAverage() {
      return this.dashboardTrainerInfos.reduce((a, c) => a + c.medicalHistory, 0) / this.dashboardTrainerInfos.length
    },
    workoutsOnAverage() {
      return this.dashboardTrainerInfos.reduce((a, c) => a + c.workouts, 0) / this.dashboardTrainerInfos.length
    },
    quoteOnAverage() {
      return this.dashboardTrainerInfos.reduce((a, c) => a + c.quote, 0) / this.dashboardTrainerInfos.length
    },
    workoutsPerCustomerOnAverage() {
      return this.dashboardTrainerInfos.reduce((a, c) => a + c.workoutsPerCustomer, 0) / this.dashboardTrainerInfos.length
    },
    interactionsPerEmployeeOnAverage() {
      return this.dashboardTrainerInfos.reduce((a, c) => a + c.interactions.totalInteractions, 0) / this.dashboardTrainerInfos.length
    },
    filteredDashboardTrainerInfos() {
      let arr = JSON.parse(JSON.stringify(this.dashboardTrainerInfos))
      arr = arr.map((u) => {
        u.studio = u.studio.map((id) => this.storeStudios.find((s) => s._id === id)?.name || "zus. Team").join(", ")
        if (this.percentagesTo100 && u.percentage < 100) {
          u.contracts = Math.round((u.contracts / u.percentage) * 100)
          u.interactions.totalInteractions = Math.round((u.interactions.totalInteractions / u.percentage) * 100)
          u.medicalHistory = Math.round((u.medicalHistory / u.percentage) * 100)
          u.workouts = Math.round((u.workouts / u.percentage) * 100)
          u.workoutsPerCustomer = Math.round((u.workoutsPerCustomer / u.percentage) * 100)
          u.upgrades = Math.round((u.upgrades / u.percentage) * 100)
          u.percentage = 100
        }
        return u
      })
      arr = arr.filter((u) => !u.fullName.toLowerCase().includes("roodytrainer"))
      if (this.activeSorting) {
        arr.sort((a, b) => b[this.activeSorting] - a[this.activeSorting])
      }
      return arr
    },
  },
  watch: {
    async studios() {
      this.finishedLoading = false
      this.dashboardTrainerInfos = await api.fetchTrainerDashboardInfos({
        studios: this.studios.map((s) => s._id),
        range: this.datePickerRange,
      })
      this.finishedLoading = true
    },
    async datePickerRange() {
      if (this.datePickerRange[0] && this.datePickerRange[1]) {
        this.finishedLoading = false
        this.dashboardTrainerInfos = await api.fetchTrainerDashboardInfos({
          studios: this.studios.map((s) => s._id),
          range: this.datePickerRange,
        })
        this.finishedLoading = true
      }
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep .apexcharts-menu-open {
  background-color: #212121 !important;
  height: 80vh;
}
.trainer-statistics {
  &__table {
    width: 100%;
  }
}
</style>
